// ══════════════════════════════════════════════════════════════════
// CartHub — Popup on-site (carrello): comportamento, colori, testi.
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api, Ic, LegendInfoIcon, useMediaMinWidth } from "../shared";
import {
  getPopupDefaultsForDashboard,
  POPUP_CANONICAL_DEFAULT_SETS,
  normalizeStoredAgainstCanonical,
} from "../i18n/popupDefaults";

/** Totale esempio (€) per anteprima % con tetto importo — allinea al messaggio nel copy i18n. */
const ON_SITE_PREVIEW_SAMPLE_SUBTOTAL = 4329;

function roundPopupPercentJs(x) {
  return Math.round(x * 10000) / 10000;
}

/** Replica `computeOnsitePopupDiscountPercent` in `onsiteOfferService.js` per anteprima dashboard. */
function computeOnsitePopupDiscountPercentPreview(
  cartValue,
  popupMaxPercent,
  email1CouponPercent,
  maxDiscountAmountRaw
) {
  const popupMax = Number(popupMaxPercent);
  const email1cp = Number(email1CouponPercent);
  const capPercent = Math.min(
    Number.isFinite(popupMax) ? popupMax : 10,
    email1cp > 0 ? email1cp : Number.isFinite(popupMax) ? popupMax : 10
  );
  let pct = Math.max(1, capPercent);
  if (maxDiscountAmountRaw == null || String(maxDiscountAmountRaw).trim() === "") {
    return roundPopupPercentJs(pct);
  }
  const maxAmt = parseFloat(String(maxDiscountAmountRaw).replace(",", "."));
  if (!Number.isFinite(maxAmt) || maxAmt <= 0) return roundPopupPercentJs(pct);
  const subtotal = parseFloat(cartValue);
  if (!(subtotal > 0)) return roundPopupPercentJs(pct);
  const discountFromPct = (subtotal * pct) / 100;
  const capped = Math.min(discountFromPct, maxAmt);
  let effPct = (capped / subtotal) * 100;
  effPct = Math.max(0.01, Math.min(effPct, pct));
  return roundPopupPercentJs(effPct);
}

function formatPopupPercentDisplay(rawPct) {
  const n = Number(rawPct);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return String(Math.round(n * 100) / 100).replace(/\.?0+$/, "");
}

function interpolate(tpl, percentStr, min) {
  const mInt = Math.round(Number(min));
  return String(tpl || "")
    .split("{percent}")
    .join(String(percentStr))
    .split("{minutes}")
    .join(String(Number.isFinite(mInt) ? mInt : 0));
}

/** Callout “esempio” (icona lampadina + titolo + testo) — tab Popup carrello soglie. */
function OnsiteThresholdExample({ title, body, id }) {
  return (
    <div className="ch-onsite-example" role="note" id={id}>
      <span className="ch-onsite-example__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M9 18h6M10 22h4M12 3a5 5 0 00-2.5 9.3V15h5v-2.7A5 5 0 0012 3z" />
        </svg>
      </span>
      <div style={{ minWidth: 0 }}>
        {title != null && String(title).trim() !== "" ? (
          <div className="ch-onsite-example__title">{title}</div>
        ) : null}
        <p className="ch-onsite-example__body">{body}</p>
      </div>
    </div>
  );
}

/** Icona smartphone (viewport mobile) — prima del toggle «mostra su mobile». */
function OnsiteMobileDeviceIcon() {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="7" y="2.5" width="10" height="19" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 5.5h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="12" cy="18.5" r="0.9" fill="currentColor" />
    </svg>
  );
}

/** Stesso pattern della legenda stati carrelli: desktop orizzontale, mobile collassabile. */
function OnSitePopupParamsHelpLegend() {
  const { t } = useTranslation("onsite");
  const isDesktop = useMediaMinWidth(900);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  const panelId = "onsite-popup-params-help-panel";
  const showPanel = isDesktop || mobileOpen;

  return (
    <section className="ch-help-legend ch-help-legend--embed" aria-label={t("legend.aria")}>
      <div className="ch-help-legend__accent" aria-hidden="true" />
      {!isDesktop && (
        <button
          type="button"
          className="ch-help-legend__summary"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span className="ch-help-legend__sum-icon">
            <LegendInfoIcon size={18} />
          </span>
          <span>
            {t("legend.summaryTitle")}
            <span className="ch-help-legend__sum-hint">{t("legend.summaryHint")}</span>
          </span>
          <span className="ch-help-legend__chev" aria-hidden="true" />
        </button>
      )}
      {showPanel && (
        <div id={panelId} className="ch-help-legend__panel">
          <header className="ch-help-legend__head ch-help-legend__head--mobile-only">
            <div className="ch-help-legend__head-icon" aria-hidden="true">
              <LegendInfoIcon />
            </div>
            <div>
              <h2 className="ch-help-legend__title">{t("legend.title")}</h2>
              <p className="ch-help-legend__sub">{t("legend.subMobile")}</p>
            </div>
          </header>

          <div className="ch-help-legend__desktop">
            <div className="ch-help-legend__desktop-intro">
              <div className="ch-help-legend__head-icon" aria-hidden="true">
                <LegendInfoIcon size={20} />
              </div>
              <div>
                <h2 className="ch-help-legend__title">{t("legend.title")}</h2>
                <p className="ch-help-legend__sub">{t("legend.subDesktop")}</p>
              </div>
            </div>
            <div className="ch-help-legend__grid ch-help-legend__grid--4">
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--neutral" aria-hidden="true" />
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("legend.labelDwell")}
                </span>
                <p className="ch-help-legend__cell-text">{t("help.dwellShort")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--warning" aria-hidden="true" />
                <span className="ch-status warning">
                  <span className="status-dot" />
                  {t("legend.labelTtl")}
                </span>
                <p className="ch-help-legend__cell-text">{t("help.ttlShort")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--success" aria-hidden="true" />
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("legend.labelMaxPct")}
                </span>
                <p className="ch-help-legend__cell-text">{t("help.maxPctShort")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--pa" aria-hidden="true" />
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("legend.labelMaxAmount")}
                </span>
                <p className="ch-help-legend__cell-text">{t("help.maxAmountShort")}</p>
              </div>
            </div>
          </div>

          <div className="ch-help-legend__list">
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--neutral" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("legend.labelDwell")}
                </span>
                <p className="ch-help-legend__text">{t("help.dwell")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--warning" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status warning">
                  <span className="status-dot" />
                  {t("legend.labelTtl")}
                </span>
                <p className="ch-help-legend__text">{t("help.ttl")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--success" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("legend.labelMaxPct")}
                </span>
                <p className="ch-help-legend__text">{t("help.maxPct")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--pa" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("legend.labelMaxAmount")}
                </span>
                <p className="ch-help-legend__text">{t("help.maxAmount")}</p>
              </div>
            </div>
          </div>

          <p className="ch-help-legend__tip">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4 8V6a2 2 0 012-2h2M20 8V6a2 2 0 00-2-2h-2M4 16v2a2 2 0 002 2h2M20 16v2a2 2 0 01-2 2h-2" strokeLinecap="round" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>{t("help.paramsTip")}</span>
          </p>
        </div>
      )}
    </section>
  );
}

function ColorField({ label, value, onChange, fallbackHex }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(String(value || "").trim()) ? String(value).trim() : fallbackHex;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          title={label}
          style={{
            width: 48,
            height: 36,
            padding: 0,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            cursor: "pointer",
          }}
        />
        <input className="ch-input" style={{ maxWidth: 120 }} value={value} onChange={(e) => onChange(e.target.value)} placeholder={fallbackHex} />
      </div>
    </div>
  );
}

export default function OnSitePopupSettings({ shopId }) {
  const { t, i18n } = useTranslation("onsite");
  const popupDefs = useMemo(() => getPopupDefaultsForDashboard(i18n.language), [i18n.language]);
  const isDesktop = useMediaMinWidth(900);

  /** Flag `onsite_popup_enabled` nel DB: aggiornato solo dal plugin WP, non da questa pagina. */
  const [cloudPopupEnabled, setCloudPopupEnabled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [dwell, setDwell] = useState(90);
  const [ttl, setTtl] = useState(30);
  const [maxPct, setMaxPct] = useState(10);
  const [maxDiscountAmt, setMaxDiscountAmt] = useState("");
  const [storeCurrency, setStoreCurrency] = useState("EUR");
  const [primaryColor, setPrimaryColor] = useState("#0A85CF");
  const [cardBackground, setCardBackground] = useState("#ffffff");
  const [contentTextColor, setContentTextColor] = useState("#111827");
  const [ctaTextColor, setCtaTextColor] = useState("#ffffff");
  const [dismissTextColor, setDismissTextColor] = useState("#111827");
  const [textAlign, setTextAlign] = useState("left");
  const [titleTpl, setTitleTpl] = useState("");
  const [bodyTpl, setBodyTpl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [dismissLabel, setDismissLabel] = useState("");
  const [minCartTotal, setMinCartTotal] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoBackground, setLogoBackground] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [email1CouponPct, setEmail1CouponPct] = useState(0);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    api(`/settings/${shopId}`)
      .then((d) => {
        if (d.email1_coupon_percent != null && d.email1_coupon_percent !== "") {
          const e1 = Number(d.email1_coupon_percent);
          setEmail1CouponPct(Number.isFinite(e1) ? e1 : 0);
        } else {
          setEmail1CouponPct(0);
        }
        if (d.onsite_popup_enabled !== undefined) setCloudPopupEnabled(!!d.onsite_popup_enabled);
        if (d.onsite_popup_mobile_enabled !== undefined) setMobile(!!d.onsite_popup_mobile_enabled);
        if (d.onsite_popup_min_dwell_seconds != null) setDwell(Number(d.onsite_popup_min_dwell_seconds) || 90);
        if (d.onsite_popup_ttl_minutes != null) setTtl(Number(d.onsite_popup_ttl_minutes) || 30);
        if (d.onsite_popup_max_percent != null) setMaxPct(Number(d.onsite_popup_max_percent) || 10);
        if (d.onsite_popup_max_discount_amount != null && d.onsite_popup_max_discount_amount !== "") {
          setMaxDiscountAmt(String(d.onsite_popup_max_discount_amount));
        } else {
          setMaxDiscountAmt("");
        }
        const cur = d.price_alert_plugin_currency != null ? String(d.price_alert_plugin_currency).trim() : "";
        setStoreCurrency(/^[A-Za-z]{3}$/.test(cur) ? cur.toUpperCase() : "EUR");
        if (d.onsite_popup_primary_color) setPrimaryColor(String(d.onsite_popup_primary_color).trim() || "#0A85CF");
        if (d.onsite_popup_card_background) setCardBackground(String(d.onsite_popup_card_background).trim() || "#ffffff");
        if (d.onsite_popup_content_text_color) setContentTextColor(String(d.onsite_popup_content_text_color).trim() || "#111827");
        if (d.onsite_popup_cta_text_color) setCtaTextColor(String(d.onsite_popup_cta_text_color).trim() || "#ffffff");
        if (d.onsite_popup_dismiss_text_color) setDismissTextColor(String(d.onsite_popup_dismiss_text_color).trim() || "#111827");
        if (d.onsite_popup_text_align) {
          const a = String(d.onsite_popup_text_align).toLowerCase();
          setTextAlign(a === "center" || a === "right" ? a : "left");
        }
        const { titles, bodies, ctas, dismisses } = POPUP_CANONICAL_DEFAULT_SETS;
        setTitleTpl(
          normalizeStoredAgainstCanonical(d.onsite_popup_title_template, titles)
        );
        setBodyTpl(normalizeStoredAgainstCanonical(d.onsite_popup_body_template, bodies));
        setCtaLabel(normalizeStoredAgainstCanonical(d.onsite_popup_cta_label, ctas));
        setDismissLabel(normalizeStoredAgainstCanonical(d.onsite_popup_dismiss_label, dismisses));
        setLogoUrl(d.email_logo_url != null ? String(d.email_logo_url) : "");
        setLogoBackground(
          d.onsite_popup_logo_background != null && String(d.onsite_popup_logo_background).trim() !== ""
            ? String(d.onsite_popup_logo_background).trim()
            : ""
        );
        if (d.onsite_popup_min_cart_total != null && String(d.onsite_popup_min_cart_total).trim() !== "") {
          const mc = Number(d.onsite_popup_min_cart_total);
          setMinCartTotal(Number.isFinite(mc) && mc > 0 ? String(mc) : "");
        } else {
          setMinCartTotal("");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shopId]);

  const preview = useMemo(() => {
    const min = ttl;
    const pctNum = computeOnsitePopupDiscountPercentPreview(
      ON_SITE_PREVIEW_SAMPLE_SUBTOTAL,
      maxPct,
      email1CouponPct,
      maxDiscountAmt
    );
    const pctStr = formatPopupPercentDisplay(pctNum);
    const d = popupDefs;
    const title = interpolate(titleTpl.trim() || d.title_template, pctStr, min);
    const body = interpolate(bodyTpl.trim() || d.body_template, pctStr, min);
    const cta = ctaLabel.trim() || d.cta_label;
    const dis = dismissLabel.trim() || d.dismiss_label;
    const cardBg = /^#[0-9A-Fa-f]{6}$/.test(cardBackground.trim()) ? cardBackground.trim() : "#ffffff";
    const txtCol = /^#[0-9A-Fa-f]{6}$/.test(contentTextColor.trim()) ? contentTextColor.trim() : "#111827";
    const ctaCol = /^#[0-9A-Fa-f]{6}$/.test(ctaTextColor.trim()) ? ctaTextColor.trim() : "#ffffff";
    const disCol = /^#[0-9A-Fa-f]{6}$/.test(dismissTextColor.trim()) ? dismissTextColor.trim() : "#111827";
    const prim = /^#[0-9A-Fa-f]{6}$/.test(primaryColor.trim()) ? primaryColor.trim() : "#0A85CF";
    const align = textAlign === "center" || textAlign === "right" ? textAlign : "left";
    const rowJustify = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
    return { title, body, cta, dis, cardBg, txtCol, ctaCol, disCol, prim, align, rowJustify };
  }, [
    titleTpl,
    bodyTpl,
    ctaLabel,
    dismissLabel,
    maxPct,
    ttl,
    cardBackground,
    contentTextColor,
    ctaTextColor,
    dismissTextColor,
    primaryColor,
    textAlign,
    maxDiscountAmt,
    email1CouponPct,
    popupDefs,
  ]);

  const save = async () => {
    if (!shopId) return;
    setSaving(true);
    try {
      await api(`/settings/${shopId}`, {
        method: "PUT",
        body: JSON.stringify({
          onsite_popup_mobile_enabled: mobile,
          onsite_popup_min_dwell_seconds: Math.min(600, Math.max(15, parseInt(String(dwell), 10) || 90)),
          onsite_popup_ttl_minutes: Math.min(180, Math.max(5, parseInt(String(ttl), 10) || 30)),
          onsite_popup_max_percent: Math.min(25, Math.max(1, parseInt(String(maxPct), 10) || 10)),
          onsite_popup_max_discount_amount: (() => {
            const t = String(maxDiscountAmt).trim();
            if (!t) return null;
            const n = parseFloat(t.replace(",", "."));
            if (!Number.isFinite(n) || n <= 0) return null;
            return Math.round(Math.min(n, 999999999999.99) * 100) / 100;
          })(),
          onsite_popup_min_cart_total: (() => {
            const t = String(minCartTotal).trim();
            if (!t) return null;
            const n = parseFloat(t.replace(",", "."));
            if (!Number.isFinite(n) || n <= 0) return null;
            return Math.round(Math.min(n, 999999999999.99) * 100) / 100;
          })(),
          onsite_popup_pdp_require_product_threshold: false,
          onsite_popup_min_pdp_product_total: null,
          onsite_popup_primary_color: primaryColor,
          onsite_popup_card_background: cardBackground,
          onsite_popup_content_text_color: contentTextColor,
          onsite_popup_cta_text_color: ctaTextColor,
          onsite_popup_dismiss_text_color: dismissTextColor,
          onsite_popup_text_align: textAlign,
          onsite_popup_title_template: titleTpl.trim() === "" ? null : titleTpl.trim(),
          onsite_popup_body_template: bodyTpl.trim() === "" ? null : bodyTpl.trim(),
          onsite_popup_cta_label: ctaLabel.trim() === "" ? null : ctaLabel.trim(),
          onsite_popup_dismiss_label: dismissLabel.trim() === "" ? null : dismissLabel.trim(),
          email_logo_url: logoUrl.trim() === "" ? null : logoUrl.trim(),
          onsite_popup_logo_background: logoBackground.trim() === "" ? null : logoBackground.trim(),
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error("save onsite popup settings", e);
    } finally {
      setSaving(false);
    }
  };

  if (!shopId) return null;

  return (
    <div className="ch-onsite-popup-settings">
      <div className="ch-card" style={{ marginBottom: 16 }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#0A85CF", display: "flex" }}>{Ic.popup}</span>
          {t("page.title")}
        </h2>
        <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16, lineHeight: 1.55 }}>{t("page.intro")}</p>
        <div style={{ marginBottom: 16 }}>
          <OnSitePopupParamsHelpLegend />
        </div>
        {loading ? (
          <div style={{ fontSize: 14, color: "#9ca3af", padding: "12px 0" }}>{t("page.loading")}</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isDesktop ? "minmax(0, 1fr) minmax(280px, 400px)" : "1fr",
              gap: isDesktop ? 28 : 0,
              alignItems: "start",
            }}
          >
            <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("page.behavior")}</h3>
            <div
              style={{
                marginBottom: 16,
                padding: "12px 14px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.55,
              }}
            >
              <strong style={{ color: "#111827" }}>{t("page.cloudStatusTitle")}</strong>
              <div style={{ marginTop: 8 }}>
                {t("page.cloudStatusLabel")}{" "}
                <span className={cloudPopupEnabled ? "ch-status success" : "ch-status neutral"} style={{ display: "inline-flex", marginLeft: 4 }}>
                  <span className="status-dot" />
                  {cloudPopupEnabled ? t("page.active") : t("page.inactive")}
                </span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280" }}>
                {t("page.cloudStatusHint")} <strong>{t("page.cloudStatusHintStrong")}</strong>
                {t("page.cloudStatusHint2")}
              </p>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "16px 0 8px", color: "#1a1a1a" }}>{t("page.thresholdsTitle")}</h3>
            <OnsiteThresholdExample
              id="onsite-threshold-example-cart"
              title={t("page.thresholdExampleCartTitle")}
              body={t("page.thresholdExampleCartBody")}
            />
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldMinCartTotal")}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <input
                  className="ch-input"
                  type="number"
                  min={0}
                  step="0.01"
                  value={minCartTotal}
                  onChange={(e) => setMinCartTotal(e.target.value)}
                  style={{ maxWidth: 160 }}
                />
                <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>{storeCurrency}</span>
              </div>
              <p style={{ fontSize: 12, color: "#9ca3af", margin: "8px 0 0", lineHeight: 1.45 }}>{t("page.fieldMinCartTotalHint")}</p>
            </div>
            <div className="ch-onsite-mobile-toggle-row">
              <span className="ch-onsite-mobile-toggle-row__icon" aria-hidden="true">
                <OnsiteMobileDeviceIcon />
              </span>
              <label className="ch-toggle" htmlFor="onsite-popup-mobile-enabled">
                <input
                  id="onsite-popup-mobile-enabled"
                  type="checkbox"
                  checked={mobile}
                  onChange={(e) => setMobile(e.target.checked)}
                />
                <span className="ch-toggle-track" />
              </label>
              <label className="ch-onsite-mobile-toggle-row__label" htmlFor="onsite-popup-mobile-enabled">
                {t("page.mobileCheckbox")}
              </label>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
                alignItems: "stretch",
              }}
            >
              <div style={{ flex: "1 1 0", minWidth: "min(100%, 140px)" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldDwell")}</div>
                <input className="ch-input" type="number" min={15} max={600} value={dwell} onChange={(e) => setDwell(e.target.value)} style={{ width: "100%" }} />
              </div>
              <div style={{ flex: "1 1 0", minWidth: "min(100%, 140px)" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldTtl")}</div>
                <input className="ch-input" type="number" min={5} max={180} value={ttl} onChange={(e) => setTtl(e.target.value)} style={{ width: "100%" }} />
              </div>
              <div style={{ flex: "1 1 0", minWidth: "min(100%, 140px)" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldMaxPct")}</div>
                <input className="ch-input" type="number" min={1} max={25} value={maxPct} onChange={(e) => setMaxPct(e.target.value)} style={{ width: "100%" }} />
                <p style={{ fontSize: 12, color: "#9ca3af", margin: "8px 0 0", lineHeight: 1.45 }}>{t("page.fieldMaxPctHint")}</p>
              </div>
              <div style={{ flex: "1 1 0", minWidth: "min(100%, 140px)" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldMaxDiscountAmount")}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <input
                    className="ch-input"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder=""
                    value={maxDiscountAmt}
                    onChange={(e) => setMaxDiscountAmt(e.target.value)}
                    style={{ flex: "1 1 auto", minWidth: 80, maxWidth: "100%" }}
                  />
                  <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500, flexShrink: 0 }}>{storeCurrency}</span>
                </div>
                <p style={{ fontSize: 12, color: "#9ca3af", margin: "8px 0 0", lineHeight: 1.45 }}>{t("page.fieldMaxDiscountAmountHint")}</p>
              </div>
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("page.logoUrlTitle")}</h3>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.5 }}>{t("page.logoUrlHint")}</p>
            <input
              className="ch-input"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder={t("page.logoUrlPlaceholder")}
              style={{ width: "100%", marginBottom: 16 }}
            />

            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("page.logoBgTitle")}</h3>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.5 }}>{t("page.logoBgHint")}</p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
              <input
                type="color"
                value={/^#[0-9A-Fa-f]{6}$/.test(logoBackground.trim()) ? logoBackground.trim() : "#f1f5f9"}
                onChange={(e) => setLogoBackground(e.target.value)}
                title={t("page.logoBgTitlePick")}
                style={{
                  width: 48,
                  height: 36,
                  padding: 0,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              />
              <input
                className="ch-input"
                style={{ maxWidth: 120 }}
                value={logoBackground}
                onChange={(e) => setLogoBackground(e.target.value)}
                placeholder={t("page.logoBgPlaceholder")}
              />
              <button type="button" className="ch-btn" onClick={() => setLogoBackground("")} style={{ fontSize: 13 }}>
                {t("page.logoBgClear")}
              </button>
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("page.appearanceTitle")}</h3>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>{t("page.appearanceHint")}</p>
            <ColorField label={t("page.colorCardBg")} value={cardBackground} onChange={setCardBackground} fallbackHex="#ffffff" />
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.textAlignLabel")}</div>
              <select className="ch-input" style={{ maxWidth: 220 }} value={textAlign} onChange={(e) => setTextAlign(e.target.value)}>
                <option value="left">{t("page.alignLeft")}</option>
                <option value="center">{t("page.alignCenter")}</option>
                <option value="right">{t("page.alignRight")}</option>
              </select>
            </div>
            <ColorField label={t("page.colorContentText")} value={contentTextColor} onChange={setContentTextColor} fallbackHex="#111827" />
            <ColorField label={t("page.colorCtaFill")} value={primaryColor} onChange={setPrimaryColor} fallbackHex="#0A85CF" />
            <ColorField label={t("page.colorCtaText")} value={ctaTextColor} onChange={setCtaTextColor} fallbackHex="#ffffff" />
            <ColorField label={t("page.colorDismissText")} value={dismissTextColor} onChange={setDismissTextColor} fallbackHex="#111827" />

            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("page.textsTitle")}</h3>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px" }}>
              {t("page.textsHint")} <code>{"{percent}"}</code> {t("page.textsHint2")}{" "}
              <code>{"{minutes}"}</code> {t("page.textsHint3")}
            </p>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldTitle")}</div>
              <input
                className="ch-input"
                value={titleTpl}
                onChange={(e) => setTitleTpl(e.target.value)}
                placeholder={popupDefs.title_template}
                style={{ width: "100%" }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldBody")}</div>
              <textarea
                className="ch-input"
                value={bodyTpl}
                onChange={(e) => setBodyTpl(e.target.value)}
                placeholder={popupDefs.body_template}
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldCta")}</div>
                <input
                  className="ch-input"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder={popupDefs.cta_label}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{t("page.fieldDismiss")}</div>
                <input
                  className="ch-input"
                  value={dismissLabel}
                  onChange={(e) => setDismissLabel(e.target.value)}
                  placeholder={popupDefs.dismiss_label}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <button
                type="button"
                className="ch-btn"
                onClick={save}
                disabled={saving}
                style={{
                  background: "#0A85CF",
                  color: "#fff",
                  border: "1px solid #0A85CF",
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.75 : 1,
                }}
              >
                {saving ? t("page.saveSaving") : saved ? t("page.saveDone") : t("page.saveButton")}
              </button>
            </div>
            </div>

            <div className={isDesktop ? "ch-dashboard-sticky-preview" : undefined}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("page.previewTitle")}</h3>
            <div
              style={{
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                padding: 20,
                background: "#f9fafb",
                maxWidth: 420,
              }}
            >
              <div
                style={{
                  background: preview.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 8px 32px rgba(0,0,0,.12)",
                }}
              >
                {logoUrl.trim() !== "" && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "0 0 12px",
                      ...( /^#[0-9A-Fa-f]{6}$/.test(logoBackground.trim())
                        ? { padding: "10px 14px", backgroundColor: logoBackground.trim() }
                        : { padding: 0 }),
                    }}
                  >
                    <img
                      src={logoUrl.trim()}
                      alt=""
                      style={{
                        maxHeight: 48,
                        maxWidth: 200,
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
                <h4
                  style={{
                    margin: "0 0 8px",
                    fontSize: 18,
                    fontWeight: 700,
                    color: preview.txtCol,
                    lineHeight: 1.25,
                    textAlign: preview.align,
                  }}
                >
                  {preview.title}
                </h4>
                <p
                  style={{
                    margin: "0 0 16px",
                    fontSize: 14,
                    color: preview.txtCol,
                    lineHeight: 1.5,
                    textAlign: preview.align,
                  }}
                >
                  {preview.body}
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: preview.rowJustify }}>
                  <button
                    type="button"
                    style={{
                      flex: preview.align === "center" ? "0 1 auto" : 1,
                      minWidth: 120,
                      padding: "10px 14px",
                      background: preview.prim,
                      color: preview.ctaCol,
                      border: "none",
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "default",
                    }}
                  >
                    {preview.cta}
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: "10px 14px",
                      background: "#e5e7eb",
                      color: preview.disCol,
                      border: "none",
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: "default",
                    }}
                  >
                    {preview.dis}
                  </button>
                </div>
              </div>
              <p style={{ fontSize: 11, color: "#6b7280", margin: "10px 0 0", lineHeight: 1.45 }}>{t("page.previewPercentNote")}</p>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
